import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco, stickyTh, stickyFoot } from './PanelShell'
import { FiLoader, FiDownload, FiSearch, FiX, FiStar, FiAlertTriangle, FiRefreshCw, FiChevronUp, FiChevronDown, FiFilter, FiFileText, FiSend, FiSlash, FiSmartphone, FiAward } from 'react-icons/fi'
import { LimiteAppChave } from './LimiteAppChave'
import { type BoletoLimite, type AlvoBoleto, ChipBoleto, LoteBoletoPanel, BoletoDetalhe, rotuloBoleto, VALOR_MINIMO_BOLETO } from './LimiteBoleto'

// Limite de crédito (nasceu "Ativas da semana" em 03/10/2026; renomeado em 06/10)
// — o relatório que o diretor pedia toda semana
// no painel Dados, com semana, período da estrela, limiar e filtros na tela.
// As definições de pedido × retail × NF estão no topo de
// backend/code/routes/ativas-semana.cjs — ler antes de mexer nas colunas.

interface Linha {
  codigo: number; nome: string; tipo: string; cpf: string | null; semLancamento: number | null
  grupo: string | null; distrito: string | null; setor: string | null; cidade: string | null; uf: string | null
  limite: number; revendedor: number; pedido: number | null; retail: number; nf: number | null; diferenca: number | null
  cartao: number; cartaoCap: number; pix: number; antecSemana: number; antecAnterior: number; boleto: number; boletoPago: number
  vendaPeriodo: number; estrela: boolean; zerado: boolean
  limBoleto?: string // situação do boleto do limite (LimiteBoleto.tsx) — preenchido na tela, não vem do Draco
}
interface Resultado {
  parametros: { semana: number; periodo: number; limiar: number; zerados: boolean }
  faturada: boolean; parcial: boolean; periodoSemanas: { de: number; ate: number }
  servidor: string; ms: number; linhas: Linha[]
}
interface Opcoes {
  semanas: { semana: number; periodo: number; faturada: boolean; parcial: boolean }[]
  periodos: { periodo: number; de: number; ate: number }[]
}

const fmt = (n: number | null) => n == null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const num = (n: number) => n.toLocaleString('pt-BR')
const sem = (s: number) => `${String(s).slice(0, 4)}/${String(s).slice(4)}`
// CLIENTES.CTIPO (TIPOSCLIENTES): 0 ger. distribuição, 1 ger. distrito, 2 ger. grupo (líder), 3 consultora.
const TIPOS: Record<string, string> = { '0': 'Ger. distribuição', '1': 'Ger. distrito', '2': 'Líder', '3': 'Consultora' }

const CHAVE = 'fin.ativasSemana.params'
const lerSalvo = (): Partial<{ semana: number; periodo: number; limiar: number; zerados: boolean }> => {
  try { return JSON.parse(localStorage.getItem(CHAVE) || '{}') } catch { return {} }
}

type ColKey = 'codigo' | 'nome' | 'limBoleto' | 'tipo' | 'cpf' | 'semLancamento' | 'grupo' | 'distrito' | 'setor' | 'cidade' | 'limite' | 'revendedor' | 'pedido' | 'retail' | 'diferenca' | 'cartao' | 'cartaoCap' | 'pix' | 'antecSemana' | 'antecAnterior' | 'boleto' | 'boletoPago' | 'vendaPeriodo' | 'estrela'
// Dica no cabeçalho — as definições completas estão em routes/ativas-semana.cjs.
const DICA: Partial<Record<ColKey, string>> = {
  revendedor: 'Itens × preço revendedor da tabela de preços — existe antes do faturamento; sem desconto, frete e corte',
  cartaoCap: 'PEDCARTOES.PCVALORCAP — valor capturado do cartão',
  antecSemana: 'Pago antes de mandar o pedido (PIX site / site) com esta semana no histórico',
  antecAnterior: 'Saldo de HOJE de pagamentos antecipados de semanas anteriores ainda não usado (sobras < R$ 1 ignoradas)',
  boleto: 'NF da semana em boleto (pagamento na entrega) — DOCCLIENTES tipo B',
  boletoPago: 'Quanto do boleto da NF da semana já foi pago',
}
// Réguas (pedido do diretor, 03/10/2026): tudo vem MARCADO e ele desmarca o
// que não quer ver. Desmarcar uma opção TIRA quem tem aquilo — nas formas de
// pagamento, que se sobrepõem, desmarcar Boleto tira quem tem boleto mesmo que
// também tenha cartão. "Nenhum" cobre quem não tem nenhuma das outras, pra
// régua toda marcada = lista inteira. Entre réguas os cortes se acumulam.
// Contador de cada botão = quantos têm aquilo com as OUTRAS réguas aplicadas.
type LinhaR = { tipo: string; cidade: string | null; cartao: number; pix: number; antecSemana: number; antecAnterior: number; boleto: number; vendaPeriodo: number; estrela: boolean; diferenca: number | null }
interface Opcao { k: string; rot: string; f: (l: LinhaR) => boolean; dica?: string }
// modo 'inclui' (03/10/2026, "cartão x pix acima de zero"): fica quem tem
// QUALQUER uma das marcadas — desmarcar Boleto, antecipados e Nenhum deixa
// exatamente quem pagou com cartão OU pix. Nas outras réguas as opções não se
// sobrepõem, e desmarcar tira quem tem aquilo. "Tirar quem tem boleto" agora é
// o filtro da coluna Boleto em "Igual a 0".
interface Regua { id: string; rot: string; opcoes: Opcao[]; modo?: 'inclui' }
const CAPITAL = 'CAMPO GRANDE'
const ehCapital = (l: LinhaR) => (l.cidade || '').toUpperCase() === CAPITAL
const pagou = (l: LinhaR) => l.cartao > 0 || l.pix > 0 || l.antecSemana > 0 || l.antecAnterior > 0 || l.boleto > 0
const REGUAS: Regua[] = [
  { id: 'local', rot: 'Local', opcoes: [
    { k: 'capital', rot: 'Campo Grande', f: ehCapital },
    { k: 'interior', rot: 'Interior', f: l => !ehCapital(l), dica: 'Cidade do cadastro fora de Campo Grande' },
  ] },
  { id: 'tipo', rot: 'Tipo', opcoes: Object.keys(TIPOS).map(t => ({ k: t, rot: `${t} · ${TIPOS[t]}`, f: (l: LinhaR) => l.tipo === t })) },
  { id: 'pagamento', rot: 'Pagou com', modo: 'inclui', opcoes: [
    { k: 'cartao', rot: 'Cartão', f: l => l.cartao > 0, dica: DICA.cartaoCap },
    { k: 'pix', rot: 'PIX', f: l => l.pix > 0 },
    { k: 'antecSemana', rot: 'Antecipado semana', f: l => l.antecSemana > 0, dica: DICA.antecSemana },
    { k: 'antecAnterior', rot: 'Antecipado anterior', f: l => l.antecAnterior > 0, dica: DICA.antecAnterior },
    { k: 'boleto', rot: 'Boleto', f: l => l.boleto > 0, dica: DICA.boleto },
    { k: 'nenhum', rot: 'Nenhum', f: l => !pagou(l), dica: 'Sem cartão, PIX, antecipado nem boleto' },
  ] },
  { id: 'venda', rot: 'Venda período', opcoes: [
    { k: 'com', rot: 'Com venda', f: l => l.vendaPeriodo > 0 },
    { k: 'sem', rot: 'Sem venda', f: l => l.vendaPeriodo <= 0 },
  ] },
  { id: 'estrela', rot: 'Estrela', opcoes: [
    { k: 'sim', rot: 'Estrela', f: l => l.estrela },
    { k: 'nao', rot: 'Não estrela', f: l => !l.estrela },
  ] },
  { id: 'limite', rot: 'Limite', opcoes: [
    { k: 'acima', rot: 'Acima do limite', f: l => (l.diferenca ?? 0) > 0 },
    { k: 'dentro', rot: 'Dentro do limite', f: l => (l.diferenca ?? 0) <= 0 },
  ] },
]
// desmarcadas por régua: { pagamento: Set(['boleto']) } — vazio = tudo marcado
type Desmarcadas = Record<string, Set<string>>
const tira = (l: LinhaR, d: Desmarcadas, fora?: string) =>
  REGUAS.some(r => {
    const des = d[r.id]
    if (r.id === fora || !des?.size) return false
    return r.modo === 'inclui'
      ? !r.opcoes.some(o => !des.has(o.k) && o.f(l))
      : r.opcoes.some(o => des.has(o.k) && o.f(l))
  })

const COLS: { k: ColKey; rot: string; n?: boolean; w?: string }[] = [
  { k: 'codigo', rot: 'Código', w: 'w-24' },
  { k: 'nome', rot: 'Nome', w: 'min-w-[220px]' },
  { k: 'limBoleto', rot: 'Boleto' },
  { k: 'tipo', rot: 'Tipo' },
  { k: 'cpf', rot: 'CPF' },
  { k: 'semLancamento', rot: 'Lançamento' },
  { k: 'grupo', rot: 'Grupo' },
  { k: 'distrito', rot: 'Distrito' },
  { k: 'setor', rot: 'Setor' },
  { k: 'cidade', rot: 'Cidade' },
  { k: 'limite', rot: 'Limite', n: true },
  { k: 'revendedor', rot: 'Revendedor', n: true },
  { k: 'pedido', rot: 'Pedido', n: true },
  { k: 'retail', rot: 'Retail', n: true },
  { k: 'diferenca', rot: 'Pedido − limite', n: true },
  { k: 'cartao', rot: 'Cartão (aut.)', n: true },
  { k: 'cartaoCap', rot: 'Cartão capturado', n: true },
  { k: 'pix', rot: 'PIX', n: true },
  { k: 'antecSemana', rot: 'Antecipado semana', n: true },
  { k: 'antecAnterior', rot: 'Antecipado anterior', n: true },
  { k: 'boleto', rot: 'Boleto NF', n: true },
  { k: 'boletoPago', rot: 'Boleto pago', n: true },
  { k: 'vendaPeriodo', rot: 'Venda período', n: true },
  { k: 'estrela', rot: 'Estrela' },
]

// Filtro de coluna estilo Excel: a lista de valores de cada coluna vem toda
// marcada; desmarcar um valor tira as linhas com ele. Número entra pelo valor
// formatado (como o Excel mostra).
const VAZIO = '(vazio)'

// "Filtros de Número" com valor digitado (06/10/2026). Antes só havia os atalhos
// "> 0 / = 0 / < 0": quem queria "maior que 200" digitava 200 no Pesquisar, que
// só filtrava a LISTA do menu — as linhas continuavam todas, e 194 aparecia.
// A comparação é sobre o número CRU da linha, nunca sobre o texto formatado.
type OpNum = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne' | 'entre'
interface CondNum { op: OpNum; a: number; b?: number }
const OPS: { k: OpNum; rot: string; sinal: string }[] = [
  { k: 'gt', rot: 'É maior que', sinal: '>' }, { k: 'gte', rot: 'É maior ou igual a', sinal: '≥' },
  { k: 'lt', rot: 'É menor que', sinal: '<' }, { k: 'lte', rot: 'É menor ou igual a', sinal: '≤' },
  { k: 'eq', rot: 'É igual a', sinal: '=' }, { k: 'ne', rot: 'É diferente de', sinal: '≠' },
  { k: 'entre', rot: 'Está entre', sinal: 'entre' },
]
// Vazio conta como 0 — mesma regra dos atalhos antigos.
function passaCond(v: unknown, c: CondNum): boolean {
  const n = v == null || v === '' ? 0 : Number(v)
  // centavos: 200 digitado tem de bater com 200,00 que veio do banco como 199.99999
  const r = Math.round(n * 100), a = Math.round(c.a * 100)
  switch (c.op) {
    case 'gt': return r > a
    case 'gte': return r >= a
    case 'lt': return r < a
    case 'lte': return r <= a
    case 'eq': return r === a
    case 'ne': return r !== a
    case 'entre': { const b = Math.round((c.b ?? c.a) * 100); return r >= Math.min(a, b) && r <= Math.max(a, b) }
  }
}
// "200", "1.200", "1.200,50", "-194,5", "R$ 3.190" → número; texto inválido → null.
function lerNumero(t: string): number | null {
  let s = t.replace(/R\$|\s/g, '')
  if (!s) return null
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.')
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '')
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}
const descreveCond = (c: CondNum) => c.op === 'entre'
  ? `entre ${fmt(c.a)} e ${fmt(c.b ?? c.a)}` : `${OPS.find(o => o.k === c.op)!.sinal} ${fmt(c.a)}`
const NUM_COLS = new Set(COLS.filter(c => c.n).map(c => c.k))
function valorFiltro(l: Linha, k: ColKey): string {
  const v = l[k]
  if (k === 'tipo') return `${l.tipo} · ${TIPOS[l.tipo] || ''}`
  if (k === 'estrela') return l.estrela ? 'SIM' : 'NÃO'
  if (v == null || v === '') return VAZIO
  if (NUM_COLS.has(k)) return fmt(Number(v))
  return String(v)
}

function FiltroColuna({ col, linhas, excluidos, cond, onAplicar, onOrdenar, onFechar, ancora }: {
  col: typeof COLS[0]; linhas: Linha[]; excluidos: Set<string>; cond: CondNum | null
  onAplicar: (ex: Set<string>, cond: CondNum | null) => void; onOrdenar: (desc: boolean) => void; onFechar: () => void; ancora: DOMRect
}) {
  const [busca, setBusca] = useState('')
  const [ex, setEx] = useState<Set<string>>(() => new Set(excluidos))
  const [op, setOp] = useState<OpNum>(cond?.op ?? 'gt')
  const [va, setVa] = useState(cond ? fmt(cond.a) : '')
  const [vb, setVb] = useState(cond?.b != null ? fmt(cond.b) : '')
  const na = lerNumero(va), nb = lerNumero(vb)
  const rascunho: CondNum | null = !col.n || na == null || (op === 'entre' && nb == null) ? null
    : { op, a: na, ...(op === 'entre' ? { b: nb! } : {}) }
  const invalido = (va.trim() !== '' && na == null) || (op === 'entre' && vb.trim() !== '' && nb == null)
  const caixa = useRef<HTMLDivElement>(null)
  // Valores distintos com contagem, na ordem natural da coluna (número por valor).
  const valores = useMemo(() => {
    const m = new Map<string, { n: number; ord: number | string }>()
    for (const l of linhas) {
      if (rascunho && !passaCond(l[col.k], rascunho)) continue
      const v = valorFiltro(l, col.k); const raw = l[col.k]
      const e = m.get(v) || { n: 0, ord: col.n ? Number(raw ?? -Infinity) : v }
      e.n++; m.set(v, e)
    }
    // valores já desmarcados continuam na lista, mesmo que outro filtro os tenha zerado
    for (const v of excluidos) if (!m.has(v)) m.set(v, { n: 0, ord: v })
    return [...m.entries()].sort((a, b) => typeof a[1].ord === 'number' && typeof b[1].ord === 'number'
      ? a[1].ord - b[1].ord : String(a[1].ord).localeCompare(String(b[1].ord), 'pt-BR'))
  }, [linhas, col, excluidos, rascunho?.op, rascunho?.a, rascunho?.b]) // eslint-disable-line react-hooks/exhaustive-deps
  const t = busca.trim().toLowerCase()
  const visiveis = t ? valores.filter(([v]) => v.toLowerCase().includes(t)) : valores
  const todosMarcados = visiveis.every(([v]) => !ex.has(v))
  const alternarTodos = () => setEx(prev => { const n = new Set(prev); visiveis.forEach(([v]) => todosMarcados ? n.add(v) : n.delete(v)); return n })
  // Atalhos preenchem o mesmo filtro de número (antes desmarcavam valores um a um).
  const atalho = (o: OpNum) => { setOp(o); setVa('0'); setVb('') }
  // OK com texto no Pesquisar = como no Excel: ficam SÓ os valores achados.
  const aplicar = () => {
    const fim = new Set(ex)
    if (t) valores.forEach(([v]) => { if (!v.toLowerCase().includes(t)) fim.add(v) })
    onAplicar(fim, rascunho)
    onFechar()
  }

  useEffect(() => {
    // A setinha do cabeçalho alterna sozinha (abre/fecha); fechar aqui também a faria reabrir.
    const fora = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest?.('[data-filtro-btn]')) return
      if (caixa.current && !caixa.current.contains(e.target as Node)) onFechar()
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onFechar() }
    document.addEventListener('mousedown', fora); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc) }
  }, [onFechar])

  // PLANOS (21/10/2026, subtelas): popup com respiro — seções separadas por fio,
  // rótulos ≥ 12.5px, controles 13px e OK/Cancelar em h-9. Mecânica intacta.
  const largura = 288
  const left = Math.max(8, Math.min(ancora.left, window.innerWidth - largura - 8))
  const btn = 'w-full text-left px-2.5 py-1.5 rounded-md hover:bg-secondary text-sm'
  return (
    <div ref={caixa} style={{ position: 'fixed', top: ancora.bottom + 4, left, width: largura }}
      className="z-50 rounded-xl border border-border bg-card shadow-xl p-3 text-sm text-foreground font-normal normal-case">
      <button className={btn} onClick={() => { onOrdenar(false); onFechar() }}>{col.n ? 'Classificar do Menor para o Maior' : 'Classificar de A a Z'}</button>
      <button className={btn} onClick={() => { onOrdenar(true); onFechar() }}>{col.n ? 'Classificar do Maior para o Menor' : 'Classificar de Z a A'}</button>
      <div className="border-t border-border my-2" />
      <button className={`${btn} disabled:opacity-40 disabled:hover:bg-transparent`} disabled={!excluidos.size && !cond}
        onClick={() => { onAplicar(new Set(), null); onFechar() }}>Limpar Filtro de "{col.rot}"</button>
      {col.n && <>
        <div className="px-2.5 pt-2 text-[12.5px] font-semibold text-muted-foreground">Filtros de Número</div>
        <div className="flex gap-1.5 px-1 pt-1.5">
          <select value={op} onChange={e => setOp(e.target.value as OpNum)}
            className="flex-1 min-w-0 h-8 rounded-md border border-border bg-background px-2 text-[13px]">
            {OPS.map(o => <option key={o.k} value={o.k}>{o.rot}</option>)}
          </select>
          <input value={va} onChange={e => setVa(e.target.value)} inputMode="decimal" placeholder="valor"
            onKeyDown={e => { if (e.key === 'Enter' && !invalido) aplicar() }}
            className={`w-24 h-8 rounded-md border bg-background px-2 text-[13px] text-right tabular-nums ${invalido ? 'border-red-500' : 'border-border'}`} />
        </div>
        {op === 'entre' && (
          <div className="flex items-center justify-end gap-1.5 px-1 pt-1.5 text-[13px] text-muted-foreground">
            e <input value={vb} onChange={e => setVb(e.target.value)} inputMode="decimal" placeholder="valor"
              onKeyDown={e => { if (e.key === 'Enter' && !invalido) aplicar() }}
              className="w-24 h-8 rounded-md border border-border bg-background px-2 text-[13px] text-right tabular-nums" />
          </div>
        )}
        <div className="flex gap-1.5 px-1 py-1.5">
          <button onClick={() => atalho('gt')} className="flex-1 rounded-md border border-border px-2 py-1.5 text-[12.5px] hover:bg-secondary">Maior que 0</button>
          <button onClick={() => atalho('eq')} className="flex-1 rounded-md border border-border px-2 py-1.5 text-[12.5px] hover:bg-secondary">Igual a 0</button>
          {col.k === 'diferenca' && <button onClick={() => atalho('lt')} className="flex-1 rounded-md border border-border px-2 py-1.5 text-[12.5px] hover:bg-secondary">Menor que 0</button>}
        </div>
      </>}
      <div className="border-t border-border my-2" />
      <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Pesquisar" autoFocus
        className="w-full h-9 rounded-md border border-border bg-background px-2.5 text-sm mb-1.5" />
      <div className="max-h-64 overflow-y-auto scrollbar-thin">
        <label className="flex items-center gap-2 px-1.5 py-1 cursor-pointer font-medium">
          <input type="checkbox" checked={todosMarcados} onChange={alternarTodos} /> (Selecionar Tudo)
        </label>
        {visiveis.slice(0, 1000).map(([v, e]) => (
          <label key={v} className="flex items-center gap-2 px-1.5 py-1 cursor-pointer hover:bg-secondary/50 rounded">
            <input type="checkbox" checked={!ex.has(v)} onChange={() => setEx(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n })} />
            <span className={`truncate flex-1 ${col.n ? 'tabular-nums' : ''}`} title={`${e.n} linha(s)`}>{v}</span>
          </label>
        ))}
        {visiveis.length > 1000 && <div className="px-1.5 py-1.5 text-[12.5px] text-muted-foreground">mostrando 1000 de {visiveis.length} — use a pesquisa</div>}
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-border mt-2">
        <button onClick={aplicar} disabled={invalido || (visiveis.length > 0 && valores.every(([v]) => ex.has(v)))}
          className="h-9 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-medium hover:opacity-90 disabled:opacity-50">OK</button>
        <button onClick={onFechar} className="h-9 rounded-lg border border-border px-3.5 text-sm hover:bg-secondary">Cancelar</button>
      </div>
    </div>
  )
}

// Botão compacto da régua ("Local: Todos ▾") que abre a lista com caixas de
// marcar — substituiu as fileiras de botões sempre acesos (03/10/2026: "ficou
// horrível"). Marca/desmarca na hora; o rótulo diz o que sobrou.
function ReguaBotao({ regua, fora, contagem, onAlternar, onTodos }: {
  regua: Regua; fora: Set<string>; contagem: (o: Opcao) => number
  onAlternar: (k: string) => void; onTodos: (marcar: boolean) => void
}) {
  const [aberto, setAberto] = useState<DOMRect | null>(null)
  const caixa = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!aberto) return
    const clique = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest?.(`[data-regua="${regua.id}"]`)) return
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(null)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(null) }
    document.addEventListener('mousedown', clique); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', clique); document.removeEventListener('keydown', esc) }
  }, [aberto, regua.id])

  const marcadas = regua.opcoes.filter(o => !fora.has(o.k))
  const tiradas = regua.opcoes.filter(o => fora.has(o.k))
  const resumo = !fora.size ? 'Todos' : !marcadas.length ? 'Nenhum' : marcadas.length === 1 ? marcadas[0].rot
    : regua.modo === 'inclui' && marcadas.length === 2 ? `${marcadas[0].rot} ou ${marcadas[1].rot}`
    : regua.modo !== 'inclui' && tiradas.length === 1 ? `sem ${tiradas[0].rot}` : `${marcadas.length} de ${regua.opcoes.length}`
  const ativo = fora.size > 0
  return (
    <>
      <button data-regua={regua.id} onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setAberto(a => a ? null : r) }}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition-colors whitespace-nowrap ${ativo
          ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border bg-card text-foreground hover:bg-secondary'}`}>
        <span className={ativo ? 'opacity-80' : 'text-muted-foreground'}>{regua.rot}:</span>
        <span className="font-medium">{resumo}</span>
        <FiChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {aberto && (
        <div ref={caixa} style={{ position: 'fixed', top: aberto.bottom + 4, left: Math.max(8, Math.min(aberto.left, window.innerWidth - 268)), width: 260 }}
          className="z-50 rounded-xl border border-border bg-card shadow-xl p-2.5 text-sm">
          {/* PLANOS (21/10/2026, subtelas): linhas com respiro (py-1.5), contagem 12.5px */}
          <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer font-medium rounded-md hover:bg-secondary/60">
            <input type="checkbox" checked={!fora.size} onChange={() => onTodos(!!fora.size)} /> (Selecionar Tudo)
          </label>
          <div className="border-t border-border my-1.5" />
          {regua.opcoes.map(o => (
            <label key={o.k} title={o.dica} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-md hover:bg-secondary/60">
              <input type="checkbox" checked={!fora.has(o.k)} onChange={() => onAlternar(o.k)} />
              <span className="flex-1">{o.rot}</span>
              <span className="text-[12.5px] text-muted-foreground tabular-nums">{num(contagem(o))}</span>
            </label>
          ))}
        </div>
      )}
    </>
  )
}

export function AtivasSemanaPanel() {
  const { authenticatedFetch } = useAuth()
  const salvo = useMemo(lerSalvo, [])
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null)
  const [semana, setSemana] = useState<number | null>(salvo.semana ?? null)
  const [periodo, setPeriodo] = useState<number | null>(salvo.periodo ?? null)
  const [limiar, setLimiar] = useState<number>(salvo.limiar ?? 3190)
  const [zerados, setZerados] = useState<boolean>(!!salvo.zerados)
  const [res, setRes] = useState<Resultado | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [q, setQ] = useState('')
  const [desm, setDesm] = useState<Desmarcadas>({})
  const alternarOpcao = (regua: string, k: string) => setDesm(prev => {
    const n = new Set(prev[regua] || []); n.has(k) ? n.delete(k) : n.add(k)
    return { ...prev, [regua]: n }
  })
  const [colDesm, setColDesm] = useState<Partial<Record<ColKey, Set<string>>>>({})
  const [colCond, setColCond] = useState<Partial<Record<ColKey, CondNum>>>({})
  const colAtiva = (k: ColKey) => !!(colDesm[k]?.size || colCond[k])
  const [filtroAberto, setFiltroAberto] = useState<{ k: ColKey; ancora: DOMRect } | null>(null)
  const [ord, setOrd] = useState<{ k: ColKey; desc: boolean }>({ k: 'distrito', desc: false })

  // Opções: semanas com pedido e períodos. Default = a última semana já
  // faturada (a que tem valor de pedido) e o último período fechado antes dela.
  useEffect(() => {
    authenticatedFetch('/api/ativas-semana/opcoes').then(r => r.json()).then(d => {
      if (!d.success) { setError(d.error || 'Falha ao ler o Draco'); return }
      setOpcoes(d)
      const s = salvo.semana && d.semanas.some((x: Opcoes['semanas'][0]) => x.semana === salvo.semana)
        ? salvo.semana : (d.semanas.find((x: Opcoes['semanas'][0]) => x.faturada) || d.semanas[0])?.semana
      setSemana(s ?? null)
      if (!salvo.periodo) setPeriodo((d.periodos as Opcoes['periodos']).find(p => s && p.ate < s)?.periodo ?? null)
    }).catch(() => setError('Erro de conexão'))
  }, [authenticatedFetch, salvo])

  const carregar = useCallback(async () => {
    if (!semana || !periodo) return
    setLoading(true); setError(null)
    try { localStorage.setItem(CHAVE, JSON.stringify({ semana, periodo, limiar, zerados })) } catch { /* sem storage */ }
    try {
      const p = new URLSearchParams({ semana: String(semana), periodo: String(periodo), limiar: String(limiar), zerados: zerados ? '1' : '0' })
      const d = await authenticatedFetch(`/api/ativas-semana?${p}`, { timeout: 120000 } as RequestInit).then(r => r.json())
      if (d.success) setRes(d); else setError(d.error || 'Falha ao gerar')
    } catch { setError('Erro de conexão') } finally { setLoading(false) }
  // limiar fica de fora: a estrela é recalculada aqui com vendaPeriodo, sem ir ao Draco.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedFetch, semana, periodo, zerados])
  useEffect(() => { carregar() }, [carregar])
  useEffect(() => { try { localStorage.setItem(CHAVE, JSON.stringify({ semana, periodo, limiar, zerados })) } catch { /* sem storage */ } }, [limiar]) // eslint-disable-line react-hooks/exhaustive-deps

  // Boletos do limite já gerados na semana carregada (um vivo por consultora).
  const [boletos, setBoletos] = useState<Map<number, BoletoLimite>>(new Map())
  const [lote, setLote] = useState<'gerar' | 'enviar' | 'cancelar' | null>(null)
  const [detalhe, setDetalhe] = useState<number | null>(null)
  // A aba Limite do app.twms: null = ainda não leu a chave.
  const [appAberto, setAppAberto] = useState(false)
  const [appLigado, setAppLigado] = useState<boolean | null>(null)
  useEffect(() => {
    authenticatedFetch('/api/limite-boleto/app').then(r => r.json()).then(d => { if (d.success) setAppLigado(!!d.config?.ativo) }).catch(() => { /* o botão fica sem o estado */ })
  }, [authenticatedFetch])
  const semanaRes = res?.parametros.semana
  useEffect(() => {
    setBoletos(new Map())
    if (!semanaRes) return
    authenticatedFetch(`/api/limite-boleto?semana=${semanaRes}`).then(r => r.json()).then(d => {
      if (d.success) setBoletos(new Map((d.boletos as BoletoLimite[]).map(b => [b.codigo, b])))
      else setError(d.error || 'Falha ao ler os boletos do limite')
    }).catch(() => setError('Erro de conexão ao ler os boletos do limite'))
  }, [authenticatedFetch, semanaRes])
  const atualizarBoleto = useCallback((b: BoletoLimite) => setBoletos(m => new Map(m).set(b.codigo, b)), [])
  const removerBoleto = useCallback((codigo: number) => setBoletos(m => { const n = new Map(m); n.delete(codigo); return n }), [])

  const linhas = useMemo(() => (res?.linhas || []).map(l => ({ ...l, estrela: l.vendaPeriodo >= limiar, limBoleto: rotuloBoleto(boletos.get(l.codigo)) })), [res, limiar, boletos])

  // `fora` = régua que NÃO entra no corte (pra calcular os contadores dela).
  const passa = useCallback((l: Linha, fora?: string) => {
    const t = q.trim().toLowerCase()
    return !tira(l, desm, fora) &&
      COLS.every(c => fora === `col:${c.k}` || ((!colDesm[c.k]?.size || !colDesm[c.k]!.has(valorFiltro(l, c.k))) &&
        (!colCond[c.k] || passaCond(l[c.k], colCond[c.k]!)))) &&
      (!t || l.nome.toLowerCase().includes(t) || String(l.codigo).includes(t) || (l.grupo || '').toLowerCase().includes(t) || (l.cpf || '').includes(t))
  }, [q, desm, colDesm, colCond])
  const semRegua = useMemo(() => Object.fromEntries(REGUAS.map(r => [r.id, linhas.filter(l => passa(l, r.id))])), [linhas, passa])


  const filtradas = useMemo(() => {
    const out = linhas.filter(l => passa(l))
    const { k, desc } = ord
    out.sort((a, b) => {
      const va = a[k], vb = b[k]
      const c = typeof va === 'number' || typeof vb === 'number' || typeof va === 'boolean'
        ? Number(va ?? -Infinity) - Number(vb ?? -Infinity)
        : String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR')
      return (desc ? -c : c) || a.nome.localeCompare(b.nome, 'pt-BR')
    })
    return out
  }, [linhas, passa, ord])

  const tot = useMemo(() => {
    const s = (f: (l: Linha) => number | null) => filtradas.reduce((a, l) => a + (f(l) ?? 0), 0)
    return {
      limite: s(l => l.limite), revendedor: s(l => l.revendedor), pedido: s(l => l.pedido), retail: s(l => l.retail), diferenca: s(l => l.diferenca),
      cartao: s(l => l.cartao), cartaoCap: s(l => l.cartaoCap), pix: s(l => l.pix), antecSemana: s(l => l.antecSemana),
      antecAnterior: s(l => l.antecAnterior), boleto: s(l => l.boleto), boletoPago: s(l => l.boletoPago), vendaPeriodo: s(l => l.vendaPeriodo),
      estrelas: filtradas.filter(l => l.estrela).length, acima: filtradas.filter(l => (l.diferenca ?? 0) > 0).length,
    }
  }, [filtradas])

  // Etapa 1 = quem está no filtro, sem boleto vivo e com diferença cobrável;
  // etapa 2 = quem está no filtro com boleto gerado e ainda não enviado.
  const plural = (n: number, s: string, p: string) => `${num(n)} ${n === 1 ? s : p}`
  const paraGerar = useMemo(() => {
    const alvos: AlvoBoleto[] = []; let ja = 0, dentro = 0, pequeno = 0
    for (const l of filtradas) {
      const d = Math.round((l.diferenca ?? 0) * 100) / 100
      if (boletos.has(l.codigo)) ja++
      else if (d <= 0) dentro++
      else if (d < VALOR_MINIMO_BOLETO) pequeno++
      else alvos.push({ codigo: l.codigo, nome: l.nome, valor: d })
    }
    const pulados = [
      ja && `${plural(ja, 'já tem', 'já têm')} boleto nesta semana`,
      dentro && `${plural(dentro, 'está', 'estão')} dentro do limite (Pedido − limite ≤ 0)`,
      pequeno && `${plural(pequeno, 'tem', 'têm')} diferença abaixo de R$ ${fmt(VALOR_MINIMO_BOLETO)} (o Asaas não emite)`,
    ].filter(Boolean) as string[]
    return { alvos, pulados }
  }, [filtradas, boletos]) // eslint-disable-line react-hooks/exhaustive-deps
  const paraEnviar = useMemo(() => {
    const alvos: AlvoBoleto[] = []; let enviados = 0, sem = 0, conferir = 0
    for (const l of filtradas) {
      const b = boletos.get(l.codigo)
      if (!b) sem++
      else if (b.status === 'enviado') enviados++
      else if (b.status === 'gerado') alvos.push({ codigo: l.codigo, nome: l.nome, valor: b.valor })
      else conferir++
    }
    const pulados = [
      enviados && `${plural(enviados, 'já recebeu', 'já receberam')} o boleto`,
      sem && `${plural(sem, 'não tem', 'não têm')} boleto gerado`,
      conferir && `${plural(conferir, 'precisa', 'precisam')} de conferência no Asaas`,
    ].filter(Boolean) as string[]
    return { alvos, pulados }
  }, [filtradas, boletos]) // eslint-disable-line react-hooks/exhaustive-deps
  // Cancelar em lote = quem está no filtro com boleto vivo. 'gerando'/'enviando'
  // estão no meio de uma operação; 'verificar' (sem cobrança confirmada) se
  // descarta um a um, depois de olhar no Asaas.
  const paraCancelar = useMemo(() => {
    const alvos: AlvoBoleto[] = []; let ocupado = 0, conferir = 0
    for (const l of filtradas) {
      const b = boletos.get(l.codigo)
      if (!b) continue
      if (b.status === 'verificar' || !b.paymentId) conferir++
      else if (b.status === 'gerado' || b.status === 'enviado') alvos.push({ codigo: l.codigo, nome: l.nome, valor: b.valor })
      else ocupado++
    }
    const pulados = [
      ocupado && `${plural(ocupado, 'está', 'estão')} gerando ou enviando agora — espere terminar`,
      conferir && `${plural(conferir, 'precisa', 'precisam')} de conferência no Asaas — descarte uma a uma`,
    ].filter(Boolean) as string[]
    return { alvos, pulados }
  }, [filtradas, boletos]) // eslint-disable-line react-hooks/exhaustive-deps

  const desmarcadas = [
    ...REGUAS.flatMap(r => !desm[r.id]?.size ? [] : r.modo === 'inclui'
      ? [`${r.rot}: ${r.opcoes.filter(o => !desm[r.id]!.has(o.k)).map(o => o.rot).join(' ou ') || 'nenhuma opção'}`]
      : r.opcoes.filter(o => desm[r.id]!.has(o.k)).map(o => `sem ${o.rot}`)),
    ...COLS.filter(c => colCond[c.k]).map(c => `${c.rot} ${descreveCond(colCond[c.k]!)}`),
    ...COLS.filter(c => colDesm[c.k]?.size).map(c => `${c.rot} (${colDesm[c.k]!.size} valor${colDesm[c.k]!.size > 1 ? 'es' : ''})`),
  ]
  const temFiltro = !!(q || desmarcadas.length)
  const limpar = () => { setDesm({}); setColDesm({}); setColCond({}); setQ('') }

  const [xlsxBusy, setXlsxBusy] = useState(false)
  const exportar = async () => {
    if (!res) return
    setXlsxBusy(true)
    try {
      const XLSX = await import('xlsx')
      const { semana: s, periodo: p } = res.parametros
      const cab = ['Código', 'Nome', 'Tipo', 'CPF', 'Sem. lançamento', 'Grupo', 'Distrito', 'Setor', 'Cidade', 'Limite de crédito',
        'Preço revendedor (tabela)', 'Total do pedido', 'Venda retail (normal+especial)', 'Valor da NF', res.faturada ? 'Diferença pedido − limite' : 'Diferença revendedor − limite',
        'PCVALORAUT (cartão autorizado)', 'PCVALORCAP (cartão capturado)', 'PXVALORPGTO (PIX)', 'Antecipado da semana', 'Antecipado anterior (saldo)',
        'Boleto da NF', 'Boleto pago', `Venda período ${p}`, `Estrela Dealer (≥ ${fmt(limiar)} em ${p})`]
      const aoa: (string | number | null)[][] = [cab, ...filtradas.map(l => [
        l.codigo, l.nome, l.tipo, l.cpf, l.semLancamento, l.grupo, l.distrito, l.setor, l.cidade, l.limite,
        l.revendedor, l.pedido, l.retail, l.nf, l.diferenca, l.cartao, l.cartaoCap, l.pix, l.antecSemana, l.antecAnterior,
        l.boleto, l.boletoPago, l.vendaPeriodo, l.estrela ? 'SIM' : 'NAO'])]
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      // CPF como texto (zero à esquerda) e valores com 2 casas.
      for (let r = 1; r < aoa.length; r++) {
        const cpf = ws[XLSX.utils.encode_cell({ r, c: 3 })]; if (cpf) { cpf.t = 's'; cpf.v = String(cpf.v) }
        for (let c = 9; c <= 22; c++) { const cel = ws[XLSX.utils.encode_cell({ r, c })]; if (cel) cel.z = '#,##0.00' }
      }
      ws['!cols'] = [11, 38, 6, 14, 11, 22, 24, 16, 18, 13, 14, 14, 15, 13, 15, 14, 14, 14, 14, 14, 13, 13, 14, 16].map(wch => ({ wch }))
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: cab.length - 1, r: aoa.length - 1 } }) }
      const criterios = [
        [`Fonte: Oracle DRACO (${res.servidor}), gerado no fin em ${new Date().toLocaleString('pt-BR')}`],
        [res.parametros.zerados ? `Base: todo pedido VC da semana ${s}, inclusive zerado (como a prévia do ERP)` : `Ativas: view DRADBA.VIATIVOS, semana ${s} (pedido VC com valor > 0)`],
        ['Preço revendedor: itens × PRECOS.PREPRECOREVENDEDOR da tabela do pedido. Existe antes do faturamento; sem desconto, frete e corte (na 202638 bateu com o preço faturado).'],
        ['Total do pedido: valor que a consultora paga = soma dos itens (qtde atendida × preço revendedor) − descontos + frete. Bate com a NF.'],
        ['Venda retail: PEVENDANORMAL + PEVENDAESPECIAL — preço de catálogo (definição da view VIPAGAMENTOSCLI do ERP)'],
        ['Valor da NF: DOCCLIENTES.DCVALOR da semana, para conferência'],
        [res.faturada ? 'Diferença: total do pedido − limite de crédito (negativo = abaixo do limite)' : 'Diferença: preço revendedor − limite de crédito (semana não faturada; negativo = abaixo do limite)'],
        [`Cartão: PEDCARTOES.PCVALORAUT (autorizado) e PCVALORCAP (capturado) da semana ${s} | PIX: PEDPIX.PXVALORPGTO da semana ${s}`],
        [`Antecipado da semana: pago antes do pedido (PIX site / site, histórico "SM ${String(s).slice(4)}/${String(s).slice(0, 4)}") — aplicado no extrato + saldo ainda aberto`],
        ['Antecipado anterior: saldo de HOJE de pagamentos antecipados de semanas anteriores ainda não usado (LANCLIENTES; sobras < R$ 1 ignoradas)'],
        [`Boleto: NF da semana ${s} em bloquete (DOCCLIENTES tipo B) e quanto dela já foi pago`],
        [`Estrela Dealer: venda (normal+especial) no período ${p} (semanas ${res.periodoSemanas.de} a ${res.periodoSemanas.ate}) ≥ ${fmt(limiar)}`],
        ...(res.faturada ? [] : [[`ATENÇÃO: semana ${s} ainda NÃO faturada no Draco — total do pedido e NF em branco até o faturamento; a diferença usa o preço revendedor.`]]),
        ...(temFiltro ? [[`Filtros aplicados na tela: ${[desmarcadas.length && desmarcadas.join(', '), q && `busca "${q}"`].filter(Boolean).join(' · ')} — ${filtradas.length} de ${linhas.length} linhas`]] : []),
      ]
      const ws2 = XLSX.utils.aoa_to_sheet(criterios); ws2['!cols'] = [{ wch: 120 }]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, `Limite ${s}`)
      XLSX.utils.book_append_sheet(wb, ws2, 'Critérios')
      XLSX.writeFile(wb, `Limite-de-credito-${s}.xlsx`)
    } catch (e) {
      console.error('[ativas:xlsx]', e)
      setError(e instanceof Error ? e.message : 'Falha ao exportar')
    } finally { setXlsxBusy(false) }
  }

  const ctrl = 'neu-input rounded-lg bg-card border border-border px-2 py-1.5 text-sm text-foreground'
  const aviso = 'flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-800 dark:text-amber-300'

  // Topo em 3 andares (03/10/2026): título+ações · parâmetros que vão ao Draco ·
  // filtros da lista já carregada. Antes eram 6 fileiras de botões acesos.
  // PLANOS (21/10/2026): título+subtítulo saíram daqui pro cabeçalho do
  // PanelShell; os botões viraram `acoes`; parâmetros e filtros, duas PlnBarra.
  const acoes = (
    <>
        <button onClick={carregar} disabled={loading || !semana} title="Ler de novo do Draco"
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-secondary disabled:opacity-50">
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </button>
        <button onClick={() => setAppAberto(true)} title="A aba Limite do app.twms: a consultora vê o limite e o boleto; a líder vê a equipe"
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-secondary">
          <FiSmartphone className="w-4 h-4" /> No app{appLigado == null ? '' : appLigado ? ': ligada' : ': só teste'}
        </button>
        <button onClick={() => setLote('gerar')} disabled={!res || loading || !paraGerar.alvos.length}
          title="Etapa 1: um boleto Asaas do Pedido − limite para cada consultora do filtro que ainda não tem"
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-secondary disabled:opacity-50">
          <FiFileText className="w-4 h-4" /> Gerar boletos{res ? ` (${num(paraGerar.alvos.length)})` : ''}
        </button>
        <button onClick={() => setLote('enviar')} disabled={!res || loading || !paraEnviar.alvos.length}
          title="Etapa 2: enviar pelo WhatsApp do Faturamento os boletos já gerados das consultoras do filtro"
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-secondary disabled:opacity-50">
          <FiSend className="w-4 h-4" /> Enviar boletos{res ? ` (${num(paraEnviar.alvos.length)})` : ''}
        </button>
        <button onClick={() => setLote('cancelar')} disabled={!res || loading || !paraCancelar.alvos.length}
          title="Cancelar no Asaas os boletos vivos das consultoras do filtro (para um só, clique no boleto da linha)"
          className="neu-convex inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-secondary disabled:opacity-50">
          <FiSlash className="w-4 h-4" /> Cancelar boletos{res ? ` (${num(paraCancelar.alvos.length)})` : ''}
        </button>
        <button onClick={exportar} disabled={!res || xlsxBusy || !filtradas.length}
          className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
          {xlsxBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />} Baixar Excel
        </button>
    </>
  )

  const toolbar = (
    <div className="flex flex-col gap-3">
      <PlnBarra>
        <PlnBarraRot>Draco</PlnBarraRot>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">Semana
          <select value={semana ?? ''} onChange={e => setSemana(Number(e.target.value))} className={ctrl} disabled={!opcoes}>
            {opcoes?.semanas.map(s => <option key={s.semana} value={s.semana}>{sem(s.semana)} · período {s.periodo}{s.faturada ? '' : s.parcial ? ' · faturando' : ' · não faturada'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">Período da estrela
          <select value={periodo ?? ''} onChange={e => setPeriodo(Number(e.target.value))} className={ctrl} disabled={!opcoes}>
            {opcoes?.periodos.map(p => <option key={p.periodo} value={p.periodo}>{p.periodo} · sem {String(p.de).slice(4)}–{String(p.ate).slice(4)}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">Estrela a partir de
          <input type="number" min={0} step={10} value={limiar} onChange={e => setLimiar(Math.max(0, Number(e.target.value) || 0))}
            className={`${ctrl} w-24 tabular-nums`} />
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" title="A prévia do ERP conta como ativa quem tem pedido de valor zero; a lista oficial (VIATIVOS) não.">
          <input type="checkbox" checked={zerados} onChange={e => setZerados(e.target.checked)} /> Incluir pedido zerado
        </label>
      </PlnBarra>

      {res && !res.faturada && (
        <div className={aviso}>
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>A semana {sem(res.parametros.semana)} ainda não foi faturada: o valor do pedido (com desconto e frete) só existe depois do faturamento. Até lá, a diferença com o limite usa o <b>preço revendedor</b> da tabela.</span>
        </div>
      )}
      {res?.parcial && (
        <div className={aviso}>
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Faturamento da semana em andamento: parte dos pedidos ainda está sem valor.</span>
        </div>
      )}
      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">{error}</div>}

      <PlnBarra>
        <PlnBarraRot>Filtrar</PlnBarraRot>
        {REGUAS.map(r => (
          <ReguaBotao key={r.id} regua={r} fora={desm[r.id] || new Set()}
            contagem={o => (semRegua[r.id] || []).filter(o.f).length}
            onAlternar={k => alternarOpcao(r.id, k)}
            onTodos={marcar => setDesm(prev => ({ ...prev, [r.id]: marcar ? new Set() : new Set(r.opcoes.map(o => o.k)) }))} />
        ))}
        <div className="relative">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar…" title="Nome, código, CPF ou grupo"
            className="neu-input pl-8 pr-7 py-1.5 rounded-lg bg-card border border-border text-sm w-52" />
          {q && <button onClick={() => setQ('')} title="limpar busca" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><FiX className="w-3.5 h-3.5" /></button>}
        </div>
        {temFiltro && <>
          <PlnBarraSep />
          <button onClick={limpar} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"><FiX className="w-3 h-3" /> Limpar filtros</button>
        </>}
      </PlnBarra>
    </div>
  )
  // PLANOS: a contagem "N de M ativas · estrelas · acima do limite" que ficava
  // solta à direita dos filtros virou a `nota` do bloco da tabela.
  const notaTabela = res ? `${num(filtradas.length)}${temFiltro ? ` de ${num(linhas.length)}` : ''} ativas · ${num(tot.estrelas)} estrelas · ${num(tot.acima)} acima do limite` : undefined

  const Th = ({ c }: { c: typeof COLS[0] }) => (
    <th title={DICA[c.k]}
      className={`${stickyTh} pl-3 pr-1 py-2 font-medium text-xs text-muted-foreground whitespace-nowrap select-none text-left ${c.w || ''}`}>
      <span className="flex items-center justify-between gap-2">
        <span>{c.k === 'diferenca' && res && !res.faturada ? 'Revendedor − limite' : c.rot}{ord.k === c.k && (ord.desc ? <FiChevronDown className="inline w-3 h-3 ml-0.5" /> : <FiChevronUp className="inline w-3 h-3 ml-0.5" />)}</span>
        <button data-filtro-btn title="Filtrar" aria-label={`Filtrar ${c.rot}`}
          onClick={e => {
            e.stopPropagation()
            // A posição é lida AGORA: dentro do updater do setState o currentTarget já é null
            // (foi o que derrubava a tela inteira na 1ª versão).
            const ancora = e.currentTarget.getBoundingClientRect()
            setFiltroAberto(f => f?.k === c.k ? null : { k: c.k, ancora })
          }}
          className={`shrink-0 w-[18px] h-[18px] inline-flex items-center justify-center rounded-sm border bg-card ${colAtiva(c.k) ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
          {colAtiva(c.k) ? <FiFilter className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
        </button>
      </span>
    </th>
  )
  const negCls = 'text-red-600 dark:text-red-400'

  return (
    <PanelShell titulo="Limite de crédito"
      sub="Ativas da semana: limite, pedido, retail, cartão, PIX, antecipado, boleto e estrela dealer — direto do Draco"
      icone={<FiAward />} acoes={acoes} toolbar={toolbar}>
      {appAberto && <LimiteAppChave authenticatedFetch={authenticatedFetch} onClose={() => setAppAberto(false)} onMudou={setAppLigado} />}
      {lote && res && (
        <LoteBoletoPanel modo={lote} semana={res.parametros.semana} estimado={!res.faturada}
          alvos={{ gerar: paraGerar, enviar: paraEnviar, cancelar: paraCancelar }[lote].alvos}
          pulados={{ gerar: paraGerar, enviar: paraEnviar, cancelar: paraCancelar }[lote].pulados}
          boletos={boletos} authenticatedFetch={authenticatedFetch}
          onAtualizar={atualizarBoleto} onRemovido={removerBoleto} onClose={() => setLote(null)} />
      )}
      {detalhe != null && boletos.get(detalhe) && (
        <BoletoDetalhe b={boletos.get(detalhe)!} authenticatedFetch={authenticatedFetch}
          onAtualizar={atualizarBoleto} onRemovido={removerBoleto} onClose={() => setDetalhe(null)} />
      )}
      {filtroAberto && (() => {
        const col = COLS.find(c => c.k === filtroAberto.k)!
        return <FiltroColuna col={col} ancora={filtroAberto.ancora}
          linhas={linhas.filter(l => passa(l, `col:${col.k}`))}
          excluidos={colDesm[col.k] || new Set()}
          cond={colCond[col.k] || null}
          onAplicar={(ex, cond) => {
            setColDesm(prev => ({ ...prev, [col.k]: ex }))
            setColCond(prev => { const n = { ...prev }; if (cond) n[col.k] = cond; else delete n[col.k]; return n })
          }}
          onOrdenar={desc => setOrd({ k: col.k, desc })}
          onFechar={() => setFiltroAberto(null)} />
      })()}
      {!res && loading ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground gap-2"><FiLoader className="w-5 h-5 animate-spin" /> lendo o Draco…</div>
      ) : res ? (
        <PlnBloco titulo="Consultoras ativas" nota={notaTabela} className="flex-1 min-h-0">
        <div className={`flex-1 min-h-0 overflow-auto scrollbar-thin ${loading ? 'opacity-60' : ''}`}>
          <table className="w-full text-sm">
            <thead><tr>{COLS.map(c => <Th key={c.k} c={c} />)}</tr></thead>
            <tbody>
              {filtradas.map(l => (
                <tr key={l.codigo} className="border-t border-border/60 hover:bg-secondary/30">
                  <td className="px-3 py-1.5 tabular-nums">{l.codigo}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{l.nome}{l.zerado && <span className="neu-tag ml-1.5 text-xs rounded-full bg-secondary text-muted-foreground px-1.5 py-0.5">zerado</span>}</td>
                  <td className="px-3 py-1.5"><ChipBoleto b={boletos.get(l.codigo)} onClick={() => setDetalhe(l.codigo)} /></td>
                  <td className="px-3 py-1.5" title={TIPOS[l.tipo]}>{l.tipo}</td>
                  <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{l.cpf}</td>
                  <td className="px-3 py-1.5 tabular-nums">{l.semLancamento}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{l.grupo}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{l.distrito}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{l.setor}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{l.cidade}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.limite)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.revendedor)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums" title={l.nf != null && Math.abs((l.pedido ?? 0) - l.nf) > 0.02 ? `NF: ${fmt(l.nf)}` : undefined}>{l.pedido == null ? <span className="text-muted-foreground/50">—</span> : fmt(l.pedido)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.retail)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${(l.diferenca ?? 0) > 0 ? negCls + ' font-medium' : ''}`}>{l.diferenca == null ? <span className="text-muted-foreground/50">—</span> : fmt(l.diferenca)}</td>
                  {([l.cartao, l.cartaoCap, l.pix, l.antecSemana, l.antecAnterior, l.boleto, l.boletoPago]).map((v, i) => (
                    <td key={i} className="px-3 py-1.5 text-right tabular-nums">{v ? fmt(v) : <span className="text-muted-foreground/50">—</span>}</td>
                  ))}
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(l.vendaPeriodo)}</td>
                  <td className="px-3 py-1.5">{l.estrela && <span className="neu-tag inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-medium"><FiStar className="w-3 h-3" />SIM</span>}</td>
                </tr>
              ))}
              {!filtradas.length && <tr><td colSpan={COLS.length} className="px-3 py-10 text-center text-muted-foreground">Ninguém no filtro.</td></tr>}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className={`${stickyFoot} px-3 py-2`} colSpan={10}>Total · {num(filtradas.length)} ativas</td>
                {[tot.limite, tot.revendedor, res.faturada ? tot.pedido : null, tot.retail, tot.diferenca, tot.cartao, tot.cartaoCap, tot.pix,
                  tot.antecSemana, tot.antecAnterior, tot.boleto, tot.boletoPago, tot.vendaPeriodo].map((v, i) => (
                  <td key={i} className={`${stickyFoot} px-3 py-2 text-right tabular-nums whitespace-nowrap`}>{v == null ? '—' : fmt(v)}</td>
                ))}
                <td className={`${stickyFoot} px-3 py-2 whitespace-nowrap`}>{num(tot.estrelas)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        </PlnBloco>
      ) : null}
    </PanelShell>
  )
}
